/**
 * Fonte da tela Margem por Anúncio.
 *
 * Desde 22/09/2026 (pedido da Mariana: "os dados precisam ser sincronizados em tempo real"):
 *   1. tenta o serviço `/api/margem-anuncios/dados` (services/margem-anuncios), que consulta
 *      `mongodb_financial.margens` NA HORA e devolve o mesmo payload do arquivo;
 *   2. se o serviço não responder, cai no arquivo estático `public/data/margem-anuncios.json.gz`
 *      (snapshot do pipeline) — e a tela DIZ que é arquivo e de quando.
 *
 * "Tempo real" = o que está em `margens` no momento da consulta. A base é recalculada pelo
 * pipeline financeiro várias vezes ao dia; a tela mostra a hora da consulta e a do último
 * cálculo. Não existe margem "da API do ML" (regra nº 3 da skill fiesta-base-dados).
 */
import type { RawMargem } from './logic'

const ARQUIVO = '/data/margem-anuncios.json.gz'
const API = '/api/margem-anuncios/dados'

export type FonteInfo = {
  origem: 'arquivo' | 'api'
  /** Quando o payload foi gerado (consulta ao vivo ou exportação do arquivo). */
  geradoEm: string
  /** Último cálculo da base `margens` (só quando vem da API). ISO. */
  ultimoCalculo?: string | null
  /** Motivo de ter caído no arquivo, quando caiu. */
  motivoArquivo?: string | null
  /** Aviso do serviço (ex.: extração em andamento, cache antigo devolvido). */
  aviso?: string | null
}

export type PeriodoApi = { ini?: string; fim?: string }

async function lerGz(url: string): Promise<unknown> {
  const r = await fetch(url, { cache: 'no-store' })
  if (!r.ok) throw new Error(`HTTP ${r.status} ao buscar ${url}`)
  // O Vite (dev) manda o .gz com Content-Encoding: gzip e o navegador já descomprime; o nginx
  // entrega cru. Olha os bytes mágicos antes de descomprimir (mesma regra de promocoes/fonte.ts).
  const bytes = new Uint8Array(await r.arrayBuffer())
  const aindaGzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b
  if (!aindaGzip) return JSON.parse(new TextDecoder().decode(bytes))
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Este navegador não tem DecompressionStream. Use Chrome/Edge 80+, Firefox 113+ ou Safari 16.4+.')
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))
  return JSON.parse(await new Response(stream).text())
}

async function daApi(periodo: PeriodoApi, fresco: boolean): Promise<{ raw: RawMargem; fonte: FonteInfo }> {
  const q = new URLSearchParams()
  if (periodo.ini) q.set('ini', periodo.ini)
  if (periodo.fim) q.set('fim', periodo.fim)
  if (fresco) q.set('fresco', '1')
  const r = await fetch(`${API}?${q.toString()}`, { credentials: 'include', cache: 'no-store' })
  if (!r.ok) {
    const corpo = (await r.json().catch(() => null)) as { detail?: string; erro?: string } | null
    throw new Error(corpo?.detail || corpo?.erro || `HTTP ${r.status}`)
  }
  const raw = (await r.json()) as RawMargem
  if (!raw?.ads?.length) throw new Error('serviço devolveu payload sem anúncios')
  const meta = raw.meta_bi as { exportado_em?: string; ultimo_calculo_margens?: string | null; aviso?: string | null } | undefined
  return {
    raw,
    fonte: {
      origem: 'api',
      geradoEm: String(raw.periodo?.gerado || meta?.exportado_em || '').trim() || 'agora',
      ultimoCalculo: meta?.ultimo_calculo_margens ?? null,
      aviso: meta?.aviso ?? null,
    },
  }
}

async function doArquivo(motivo: string): Promise<{ raw: RawMargem; fonte: FonteInfo }> {
  const raw = (await lerGz(ARQUIVO)) as RawMargem
  if (!raw?.ads?.length) throw new Error('margem-anuncios.json.gz veio sem anúncios')
  const geradoEm = String(raw.periodo?.gerado || raw.meta_bi?.exportado_em || '').trim() || 'desconhecido'
  return { raw, fonte: { origem: 'arquivo', geradoEm, motivoArquivo: motivo } }
}

/**
 * Serviço primeiro; arquivo como reserva. `fresco` ignora o cache do serviço (botão "Atualizar
 * agora"). Sem período, o serviço devolve do dia 1 do mês corrente até hoje.
 */
export async function carregaMargemAnuncios(
  periodo: PeriodoApi = {},
  fresco = false,
): Promise<{ raw: RawMargem; fonte: FonteInfo }> {
  try {
    return await daApi(periodo, fresco)
  } catch (e) {
    const motivo = e instanceof Error ? e.message : String(e)
    return doArquivo(motivo)
  }
}
